import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem, SelectGroup, SelectLabel } from "@/components/ui/select";
import { X } from "lucide-react";
import ScoreInput from "./ScoreInput";
import RatingCard from "./RatingCard";
import { getPath, SOURCE_META } from "./fieldMap";
import { SENSORY, CUP, ORIGINS, TEA_TYPES, STANDARD_GRADES, CHARACTER_OPTIONS, BUYER_FIT_OPTIONS } from "../studioApi";

function Field({ label, path, product, onChange, type = "text", placeholder, hint, textarea, className = "" }) {
  const v = getPath(product, path) ?? "";
  const src = product.field_sources?.[path];
  const tid = `field-${path.replace(/\./g, "-")}`;
  const flagged = (product._flags || []).find((f) => f.field && path.endsWith(f.field));
  return (
    <div className={className}>
      <div className="flex items-center justify-between gap-2">
        <label className="text-[11px] font-semibold text-[#6A776F] uppercase tracking-wide">{label}</label>
        <div className="flex items-center gap-1">
          {flagged && <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-800 border-amber-300" title={flagged.reason}>needs review</Badge>}
          {src && SOURCE_META[src] && <Badge variant="outline" className={`text-[10px] ${SOURCE_META[src].cls}`}>{SOURCE_META[src].label}</Badge>}
        </div>
      </div>
      {textarea ? (
        <Textarea value={v} onChange={(e) => onChange(path, e.target.value)} placeholder={placeholder} rows={2} className="mt-1 bg-white text-sm" data-testid={tid} />
      ) : (
        <Input type={type} value={v} onChange={(e) => onChange(path, e.target.value)} placeholder={placeholder} className="mt-1 bg-white h-9" data-testid={tid} />
      )}
      {hint && <div className="text-[11px] text-[#6A776F] mt-0.5">{hint}</div>}
    </div>
  );
}

function Pick({ label, path, product, onChange, options, placeholder, custom: customOpts = [] }) {
  const [customMode, setCustomMode] = useState(false);
  const v = getPath(product, path) ?? "";
  const src = product.field_sources?.[path];
  const lower = options.map((o) => o.toLowerCase());
  const extra = customOpts.filter((o) => o && !lower.includes(o.toLowerCase()));
  const all = [...options, ...extra];
  const custom = (v && !all.includes(v)) || (customMode && !v);
  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <label className="text-[11px] font-semibold text-[#6A776F] uppercase tracking-wide">{label}</label>
        {src && SOURCE_META[src] && <Badge variant="outline" className={`text-[10px] ${SOURCE_META[src].cls}`}>{SOURCE_META[src].label}</Badge>}
      </div>
      <div className="mt-1 flex gap-2">
        <Select value={custom ? "__custom" : v || "__none"} onValueChange={(val) => { setCustomMode(val === "__custom"); onChange(path, val === "__none" || val === "__custom" ? (val === "__custom" ? v : "") : val); }}>
          <SelectTrigger className="h-9 bg-white w-[140px] shrink-0" data-testid={`select-${path}`}><SelectValue placeholder={placeholder} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none">—</SelectItem>
            {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            {!!extra.length && <SelectGroup><SelectLabel className="text-[10px] uppercase tracking-wide text-[#AD8642]">Your phrases</SelectLabel>{extra.map((o) => <SelectItem key={o} value={o} data-testid={`custom-option-${path}-${o}`}>{o}</SelectItem>)}</SelectGroup>}
            <SelectItem value="__custom">Custom…</SelectItem>
          </SelectContent>
        </Select>
        <Input value={v} onChange={(e) => onChange(path, e.target.value)} placeholder="As written…" className="h-9 bg-white min-w-0" data-testid={`field-${path}`} />
      </div>
    </div>
  );
}

function Tags({ product, onChange, custom: customOpts = [] }) {
  const list = product.buyer_fit || [];
  const src = product.field_sources?.buyer_fit;
  const addTag = (t) => { t = (t || "").trim(); if (t && !list.includes(t)) onChange("buyer_fit", [...list, t]); };
  const add = (e) => {
    if (e.key !== "Enter" && e.key !== ",") return;
    e.preventDefault();
    addTag(e.target.value);
    e.target.value = "";
  };
  const remaining = BUYER_FIT_OPTIONS.filter((o) => !list.includes(o));
  const extra = customOpts.filter((o) => o && !BUYER_FIT_OPTIONS.some((b) => b.toLowerCase() === o.toLowerCase()) && !list.includes(o));
  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <label className="text-[11px] font-semibold text-[#6A776F] uppercase tracking-wide">Buyer fit</label>
        {src && SOURCE_META[src] && <Badge variant="outline" className={`text-[10px] ${SOURCE_META[src].cls}`}>{SOURCE_META[src].label}</Badge>}
      </div>
      <div className="mt-1 flex gap-2">
        <Select value="" onValueChange={addTag}>
          <SelectTrigger className="h-9 bg-white w-[190px] shrink-0" data-testid="buyer-fit-select"><SelectValue placeholder="Add from list…" /></SelectTrigger>
          <SelectContent>
            {remaining.map((o) => <SelectItem key={o} value={o} data-testid={`buyer-fit-option-${o}`}>{o}</SelectItem>)}
            {!!extra.length && <SelectGroup><SelectLabel className="text-[10px] uppercase tracking-wide text-[#AD8642]">Your phrases</SelectLabel>{extra.map((o) => <SelectItem key={o} value={o} data-testid={`buyer-fit-option-${o}`}>{o}</SelectItem>)}</SelectGroup>}
            {!remaining.length && !extra.length && <div className="px-2 py-1.5 text-xs text-[#6A776F]">All list items added</div>}
          </SelectContent>
        </Select>
        <div className="flex-1 flex flex-wrap gap-1 rounded-md border border-input bg-white px-2 py-1.5 min-h-9" data-testid="field-buyer_fit">
          {list.map((t) => <span key={t} className="inline-flex items-center gap-1 rounded bg-[#F1E5C9] px-2 py-0.5 text-xs text-[#153E32]">{t}<button type="button" onClick={() => onChange("buyer_fit", list.filter((x) => x !== t))} data-testid={`buyer-fit-remove-${t}`}><X className="h-3 w-3" /></button></span>)}
          <input onKeyDown={add} placeholder={list.length ? "Or type your own… (Enter)" : "Or type your own, e.g. Tea stalls & hotels (Enter to add)"} className="flex-1 min-w-[140px] text-sm outline-none bg-transparent" data-testid="buyer-fit-input" />
        </div>
      </div>
      <div className="text-[11px] text-[#6A776F] mt-0.5">Printed in the Buyer fit panel · up to 3 items fit comfortably.</div>
    </div>
  );
}

function ScoreText({ grp, k, label, product, onChange }) {
  const d = getPath(product, `${grp}.${k}`) || {};
  const src = product.field_sources?.[`${grp}.${k}.text`];
  const ssrc = product.field_sources?.[`${grp}.${k}.score`];
  return (
    <div className="rounded-lg border border-[#AD8642]/25 bg-white p-3" data-testid={`${grp}-${k}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0"><div className="text-xs font-semibold text-[#153E32]">{label}</div>{ssrc && SOURCE_META[ssrc] && <Badge variant="outline" className={`text-[10px] ${SOURCE_META[ssrc].cls}`} data-testid={`score-src-${grp}-${k}`}>{SOURCE_META[ssrc].label}</Badge>}</div>
        <ScoreInput size="sm" value={d.score} onChange={(v) => onChange(`${grp}.${k}.score`, v)} testid={`score-${grp}-${k}`} />
      </div>
      <div className="mt-2 flex items-center gap-2">
        <Input value={d.text || ""} onChange={(e) => onChange(`${grp}.${k}.text`, e.target.value)} placeholder="Description" className="h-8 text-sm" data-testid={`field-${grp}-${k}-text`} />
        {src && SOURCE_META[src] && <Badge variant="outline" className={`text-[10px] shrink-0 ${SOURCE_META[src].cls}`}>{SOURCE_META[src].label}</Badge>}
      </div>
    </div>
  );
}

function Section({ title, children, testid, note }) {
  return (
    <section className="rounded-xl border border-[#AD8642]/25 bg-[#FBFAF6] p-4 space-y-3" data-testid={testid}>
      <div><h3 className="font-display text-xl text-[#153E32]">{title}</h3>{note && <div className="text-[11px] text-[#6A776F]">{note}</div>}</div>
      {children}
    </section>
  );
}

export default function ProductForm({ product, onChange, policy, policyBusy, onRating, settings, isAdmin, dupes }) {
  const tax = product.tax;
  const customOpts = settings?.custom_options || {};
  return (
    <div className="space-y-4" data-testid="product-form">
      <Section title="Identity" testid="section-identity">
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Product / garden name" path="name" product={product} onChange={onChange} placeholder="e.g. Sarodamoni Premium Plus" className="sm:col-span-2" />
          <Field label="Manufacturer" path="manufacturer" product={product} onChange={onChange} placeholder="Company printed on the label" />
          <Pick label="Origin" path="origin" product={product} onChange={onChange} options={ORIGINS} placeholder="Origin" />
          <Pick label="Tea type" path="tea_type" product={product} onChange={onChange} options={TEA_TYPES} placeholder="Type" />
          <Pick label="Grade" path="grade" product={product} onChange={onChange} options={STANDARD_GRADES} placeholder="Grade" />
          <Field label="Short description" path="description" product={product} onChange={onChange} placeholder="One line under the product name" className="sm:col-span-2"
            hint={`${(product.description || "").length}/110 characters — one line on the page; longer text wraps to two smaller lines (never a second page).`} />
        </div>
        {!!(dupes || []).length && (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="duplicate-warning">
            Possible duplicate (not blocked — separate lots of the same tea are allowed): {dupes.map((d) => <span key={d.id} className="font-semibold"> {d.name}{d.lot_no ? ` · lot ${d.lot_no}` : ""} ({d.reason});</span>)}
          </div>
        )}
      </Section>

      <Section title="Invoice, lot & date" testid="section-lot">
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Invoice no." path="invoice_no" product={product} onChange={onChange} />
          <Field label="Lot no." path="lot_no" product={product} onChange={onChange} hint="Used in the sample-enquiry strip, QR and WhatsApp message" />
          <Field label="Date" path="date" product={product} onChange={onChange} type="date" />
        </div>
      </Section>

      <Section title="Commercial offer" testid="section-commercial" note="Base selling price per kg before GST. The AI never infers a price from appearance.">
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Price per kg (₹, excl. GST)" path="price_per_kg" product={product} onChange={onChange} type="number" placeholder="150.00" />
          <Field label="GST rate (%)" path="tax_rate" product={product} onChange={onChange} type="number" placeholder={settings?.tax_rate_default || "5"} hint={tax?.base ? `GST ₹${tax.gst_amount} → ₹${tax.inclusive}/kg incl.` : ""} />
          <Field label="Commercial terms (internal)" path="commercial_terms" product={product} onChange={onChange} placeholder="Optional, not printed" />
        </div>
        <RatingCard product={product} policy={policy} policyBusy={policyBusy} onRating={onRating} onText={(v) => onChange("overall_text", v)} tax={tax} />
      </Section>

      <Section title="Character & buyer fit" testid="section-character" note="Pick from the tea-trade list (or your own phrases from Settings) or type your own wording — the text box always wins.">
        <div className="grid sm:grid-cols-2 gap-3">
          <Pick label="Body" path="body" product={product} onChange={onChange} options={CHARACTER_OPTIONS.body} custom={customOpts.body} placeholder="Body" />
          <Pick label="Strength" path="strength" product={product} onChange={onChange} options={CHARACTER_OPTIONS.strength} custom={customOpts.strength} placeholder="Strength" />
          <Pick label="Briskness" path="briskness" product={product} onChange={onChange} options={CHARACTER_OPTIONS.briskness} custom={customOpts.briskness} placeholder="Briskness" />
          <Pick label="Aroma" path="aroma" product={product} onChange={onChange} options={CHARACTER_OPTIONS.aroma} custom={customOpts.aroma} placeholder="Aroma" />
        </div>
        <Tags product={product} onChange={onChange} custom={customOpts.buyer_fit} />
      </Section>

      <Section title="Tasting result cards" testid="section-cards" note="Your scores. “Let AI score” proposes values inside the price-policy range for your review — nothing is applied until you accept it.">
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="rounded-lg border border-[#AD8642]/25 bg-white p-3 space-y-2">
            <div className="flex items-center justify-between"><div className="flex items-center gap-1.5"><div className="text-xs font-semibold text-[#153E32]">Milk suitability</div>{SOURCE_META[product.field_sources?.milk_score] && <Badge variant="outline" className={`text-[10px] ${SOURCE_META[product.field_sources.milk_score].cls}`} data-testid="score-src-milk">{SOURCE_META[product.field_sources.milk_score].label}</Badge>}</div><ScoreInput size="sm" value={product.milk_score} onChange={(v) => onChange("milk_score", v)} testid="score-milk" /></div>
            <Input value={product.milk_text || ""} onChange={(e) => onChange("milk_text", e.target.value)} placeholder="Milk-tea performance" className="h-8 text-sm" data-testid="field-milk_text" />
          </div>
          <div className="rounded-lg border border-[#AD8642]/25 bg-white p-3 space-y-2">
            <div className="flex items-center justify-between"><div className="flex items-center gap-1.5"><div className="text-xs font-semibold text-[#153E32]">Commercial value</div>{SOURCE_META[product.field_sources?.value_score] && <Badge variant="outline" className={`text-[10px] ${SOURCE_META[product.field_sources.value_score].cls}`} data-testid="score-src-value">{SOURCE_META[product.field_sources.value_score].label}</Badge>}</div><ScoreInput size="sm" value={product.value_score} onChange={(v) => onChange("value_score", v)} testid="score-value" /></div>
            <Input value={product.value_text || ""} onChange={(e) => onChange("value_text", e.target.value)} placeholder="Commercial-use score" className="h-8 text-sm" data-testid="field-value_text" />
          </div>
        </div>
      </Section>

      <Section title="Sensory evaluation" testid="section-sensory" note="Dry leaf, liquor and wet leaf descriptions may come from visible appearance; taste & aroma needs your tasting or a reviewed AI draft. Scores are yours or AI-proposed within the policy range.">
        <div className="grid sm:grid-cols-2 gap-3">{SENSORY.map(([k, label]) => <ScoreText key={k} grp="sensory" k={k} label={label} product={product} onChange={onChange} />)}</div>
      </Section>

      <Section title="Cup character & ratings" testid="section-cup" note="Eight cup cards. Use “Let AI score” to fill scores and one-word descriptors inside the policy range, then adjust freely.">
        <div className="grid sm:grid-cols-2 gap-3">{CUP.map(([k, label]) => <ScoreText key={k} grp="cup" k={k} label={label} product={product} onChange={onChange} />)}</div>
      </Section>

      <Section title="Taster’s verdict & attribution" testid="section-verdict">
        <Field label="Taster’s verdict" path="verdict" product={product} onChange={onChange} textarea placeholder="Two short sentences for the customer" />
        <Field label="Attribution strip heading (optional override)" path="attribution_heading" product={product} onChange={onChange} placeholder={settings?.attribution?.default_heading} hint="Blank = Settings default. The ‘Tea Board-authorised taster’ wording appears only when the credential is verified in Settings AND tasting is confirmed below." />
        <div className="flex items-center justify-between rounded-lg border border-[#AD8642]/25 bg-white px-3 py-2">
          <div className="text-sm">
            <div className="font-semibold text-[#153E32]">Actual tasting confirmed</div>
            <div className="text-[11px] text-[#6A776F]">{settings?.attribution?.credential_verified ? "Credential verified in Settings — confirming shows the authorised-taster strip." : "Taster credential not yet verified by an administrator in Settings — the neutral heading is used regardless."}</div>
          </div>
          <Switch checked={!!product.tasting_confirmed} onCheckedChange={(v) => onChange("tasting_confirmed", v)} disabled={!isAdmin} data-testid="field-tasting_confirmed" />
        </div>
        <div className="text-xs text-[#6A776F]">Strip will read: <b className="text-[#153E32]" data-testid="strip-preview-text">{settings?.attribution?.credential_verified && product.tasting_confirmed ? settings.attribution.verified_heading : product.attribution_heading || settings?.attribution?.default_heading}</b></div>
      </Section>
    </div>
  );
}
